import { create } from 'zustand';

/**
 * How much cushion to hold before playing — the setting StreamBridge had, and
 * the reason it rode out blips this app was failing on.
 *
 * There is no universally right answer, which is exactly why it is a setting:
 * the trade is between how long a channel takes to start and how big a
 * hiccup it can absorb without you hearing it. A Fire Stick on wifi at the
 * far end of the house wants a different answer from a wired desktop, and
 * a live sports feed wants a different answer from background radio.
 *
 * `liveSync` is in SEGMENTS, not seconds — how far back from the live edge to
 * sit. It is capped at 4 on purpose: the rolling window (live-window.ts) is
 * eight segments deep, and a player sitting more than half of it back can
 * slide off the BACK of the window during a stall and start asking for
 * segments that no longer exist. The native path has no such limit because a
 * continuous pipe has no window to fall out of.
 */

export type BufferProfileId = 'fast' | 'balanced' | 'stable';

export interface BufferProfile {
  id: BufferProfileId;
  name: string;
  hint: string;
  /** hls.js: segments back from the live edge. */
  liveSync: number;
  /** hls.js: seconds of forward buffer to build. */
  maxBufferSeconds: number;
  /** ExoPlayer: [minMs, maxMs, forPlaybackMs, afterRebufferMs]. */
  native: [number, number, number, number];
  /**
   * ExoPlayer's hard ceiling on buffered BYTES.
   *
   * Durations alone are not a budget. These channels run about 6 Mbps —
   * measured from the fan-out, 46 MB in a minute — so the "most stable"
   * profile's three minutes of buffer asked a Fire TV Stick to hold roughly a
   * hundred and thirty megabytes of video. It will not, and what happens
   * instead is allocation pressure and a picture that stutters and stops:
   * a buffer setting that made buffering worse.
   *
   * Seconds are what the user is choosing; bytes are what the device can
   * actually give. Both have to be stated, and the smaller one wins.
   */
  nativeTargetBytes: number;
}

export const BUFFER_PROFILES: BufferProfile[] = [
  {
    id: 'fast',
    name: 'Fast start',
    hint: 'Starts quickest. Least tolerant of a blip.',
    liveSync: 2,
    maxBufferSeconds: 30,
    native: [5_000, 30_000, 1_500, 3_000],
    nativeTargetBytes: 8 * 1024 * 1024,     // ~10s at 6 Mbps
  },
  {
    id: 'balanced',
    name: 'Balanced',
    hint: 'The default. A few seconds of cushion.',
    liveSync: 3,
    maxBufferSeconds: 60,
    native: [15_000, 60_000, 2_500, 5_000],
    nativeTargetBytes: 20 * 1024 * 1024,    // ~26s at 6 Mbps
  },
  {
    id: 'stable',
    name: 'Most stable',
    hint: 'Rides out drops. Slower to start, further behind live.',
    liveSync: 4,
    maxBufferSeconds: 120,
    // 90s rather than 180s: past this the byte ceiling binds first anyway,
    // and a number that can never be reached is a number that misleads.
    native: [30_000, 90_000, 5_000, 10_000],
    nativeTargetBytes: 40 * 1024 * 1024,    // ~53s at 6 Mbps
  },
];

const KEY = 'n8m.buffer';
const DEFAULT: BufferProfileId = 'balanced';

function load(): BufferProfileId {
  try {
    const saved = localStorage.getItem(KEY) as BufferProfileId | null;
    return BUFFER_PROFILES.some((p) => p.id === saved) ? saved! : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

interface BufferState {
  id: BufferProfileId;
  set(id: BufferProfileId): void;
}

export const useBuffer = create<BufferState>((set) => ({
  id: load(),
  set: (id) => {
    try { localStorage.setItem(KEY, id); } catch { /* per-device convenience */ }
    set({ id });
  },
}));

/** The active profile, readable from outside React (the player needs it). */
export function bufferProfile(): BufferProfile {
  const { id } = useBuffer.getState();
  return BUFFER_PROFILES.find((p) => p.id === id) ?? BUFFER_PROFILES[1];
}
